<script lang="ts" setup>
import { mdiWifiOff } from "@mdi/js";
import { setUser as setSentryUser } from "@sentry/vue";
import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
import { useToggle } from "@vueuse/core";
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute } from "vue-router";

import { pb } from "#api/pocketbase";
import LayoutStack from "#components/layout/LayoutStack.vue";
import TheAppBar from "#components/single/TheAppBar.vue";
import TheAppConfirmDialog from "#components/single/TheAppConfirmDialog.vue";
import TheAppDebug from "#components/single/TheAppDebug.vue";
import TheAppDrawer from "#components/single/TheAppDrawer.vue";
import TheAppSnackbar from "#components/single/TheAppSnackbar.vue";
import { useAuthAccount } from "#modules/account/account.store";
import { mapRecordToAccount } from "#modules/account/account.utils";
import { useApiHealthcheck } from "#modules/app/app.queries";
import { appConfig } from "#src/config";
import { getError } from "#utilities/error";

const route = useRoute();
const accountStore = reactive(useAuthAccount());

const [showMobileDrawer, toggleMobileDrawer] = useToggle(false);

const loadingStatus = ref<"loading" | "error" | "ready">("loading");

const { error: apiHealthError, isLoading: apiHealthLoading } = useApiHealthcheck();

const loading = computed(() => {
  return loadingStatus.value === "loading" || apiHealthLoading.value;
});

onMounted(async () => {
  // TODO: Load additional data

  checkAuthentication();

  loadingStatus.value = "ready";
});

const checkAuthentication = () => {
  // Ensure auth store is cleaned up if stored auth state is invalid
  if (!pb.authStore.isValid || !pb.authStore.record) {
    pb.authStore.clear();
    return;
  }

  // Initiate refresh token workflow if existing token is valid
  pb.collection(pb.authStore.record.collectionName)
    .authRefresh()
    .catch(() => {
      pb.authStore.clear();
    });
};

/**
 * Previous auth store state (for comparisons with current state)
 *
 * By comparing previous state with current auth store update value, actions can be taken to reflect
 *   the performed action. This can work with both "immediate" or "lazy" auth store change handler,
 *   since the auth store is synchronously set upon app load (vs having to send request to API first).
 */
const prevModel = ref(pb.authStore.record);
pb.authStore.onChange(async (_token, model) => {
  const wasAuthenticated = !!prevModel.value;
  const nowAuthenticated = !!model;
  prevModel.value = model;

  if (appConfig.development) {
    // eslint-disable-next-line no-console
    console.debug("AuthStore.onChange", model, wasAuthenticated, nowAuthenticated);
  }

  // When user becomes authenticated (ie. via login), a reload should be triggered to ensure proper
  //   page state. The authenticated user will then be automatically detected/set upon load.
  if (!wasAuthenticated && nowAuthenticated) {
    const { redirectUrl } = route.query;
    const finalUrl = redirectUrl && typeof redirectUrl === "string" ? redirectUrl : "/dashboard";
    window.location.replace(finalUrl);
    return;
  }

  // When user was already authenticated and auth is updated, ensure authenticated user is updated.
  //   This will usually occur through token refresh, but may also occur on initial page load.
  if (wasAuthenticated && nowAuthenticated) {
    const account = mapRecordToAccount(model);
    accountStore.setAccount(account);
    setSentryUser({ email: account.email, id: account.id });
    return;
  }

  // When user becomes unauthenticated (ie. via logout), a reload should be triggered to ensure proper
  //   page state. The authenticated user should not be removed from cache (to avoid flickers), as it
  //   will no longer be present upon load.
  if (wasAuthenticated && !nowAuthenticated) {
    window.location.replace("/auth/login");
    return;
  }

  // When user was already unauthenticated and auth is cleared again, ensure auth store is cleared.
  if (!wasAuthenticated && !nowAuthenticated) {
    accountStore.clearAccount();
    setSentryUser(null);
    return;
  }
}, true);
</script>

<template>
  <VApp>
    <TheAppBar @toggle-drawer="toggleMobileDrawer" />

    <VProgressCircular v-if="loading" class="ma-auto" indeterminate size="80" width="6" />
    <template v-else-if="apiHealthError">
      <VMain>
        <LayoutStack align-items="center" class="w-100 h-100" justify-content="center">
          <VCard class="offline-card pa-6 w-full" color="error" variant="tonal">
            <VIcon class="d-block mx-auto" :icon="mdiWifiOff" size="48" />
            <VCardTitle>Unknown Error</VCardTitle>
            <VCardText>
              Oh no, it looks like there's an unexpected issue! Check back soon!
              <VAlert v-if="apiHealthError" class="mt-4" type="error">
                {{ getError(apiHealthError) }}
              </VAlert>
            </VCardText>
          </VCard>
        </LayoutStack>
      </VMain>
    </template>
    <template v-else>
      <TheAppDrawer
        v-if="accountStore.authenticated"
        :show-on-mobile="showMobileDrawer"
        @toggle-drawer="toggleMobileDrawer"
      />

      <VMain>
        <RouterView />
      </VMain>
    </template>

    <TheAppConfirmDialog />
    <TheAppDebug />
    <TheAppSnackbar />

    <VueQueryDevtools v-if="!appConfig.production" />
  </VApp>
</template>

<style lang="scss" scoped>
.offline-card {
  margin: auto;
  max-width: 500px;
}
</style>
